import type { LinearPathItem } from '@/sections/insights/queryBuilder/components/QuerySplitDisplay/QuerySplitVisualizer/types';
import type { FlatGraph } from '@/sections/insights/queryBuilder/types/FlatGraph';

/**
 * Linearizes a FlatGraph into a sequential path of nodes and edges
 *
 * @param flatGraph - The graph to linearize
 * @returns Array of LinearPathItem if query is linear, null if branching detected
 * @throws Error if circular path detected
 */
export function linearizePath(flatGraph: FlatGraph): LinearPathItem[] | null {
  const path: LinearPathItem[] = [];
  const visited = new Set<string>();

  // Step 1: Walk backward from root through source edges to find real start
  function findStart(
    nodeId: string,
    backwardVisited: Set<string>
  ): string | null {
    if (backwardVisited.has(nodeId)) {
      throw new Error('Circular path detected');
    }
    backwardVisited.add(nodeId);

    // Find source edges pointing TO this node
    const sourceEdges = Array.from(flatGraph.edges.values()).filter(
      edge => edge.targetId === nodeId && edge.direction === 'source'
    );

    if (sourceEdges.length > 1) {
      return null; // Branching detected
    }
    if (sourceEdges.length === 0) {
      return nodeId; // This is the start
    }

    // Follow backward to the source node
    return findStart(sourceEdges[0].sourceId, backwardVisited);
  }

  const startNodeId = findStart(flatGraph.rootId, new Set());
  if (startNodeId === null) return null; // Branching detected during backward walk

  // Step 2: Traverse forward from the start
  let currentNodeId: string | undefined = startNodeId;

  while (currentNodeId) {
    if (visited.has(currentNodeId)) {
      throw new Error('Circular path detected');
    }
    visited.add(currentNodeId);

    const node = flatGraph.nodes.get(currentNodeId);
    if (!node) break;

    path.push({ type: 'node', node });

    // Find outgoing edges (both directions use sourceId as the "from" node in the flat structure)
    const outgoingEdges = Array.from(flatGraph.edges.values()).filter(
      edge => edge.sourceId === currentNodeId
    );

    // Branching check
    if (outgoingEdges.length > 1) {
      return null;
    }

    if (outgoingEdges.length === 0) break; // End of chain

    const edge = outgoingEdges[0];
    path.push({ type: 'edge', edge });
    currentNodeId = edge.targetId;
  }

  return path;
}
